// LockedVideo.jsx — the sign-in wall, drawn as a locked player.
//
// THE SIGN-IN LIVES INSIDE THE PLAYER. One surface, no overlay, nothing hidden.
//
// It used to open the dismissible prompt over itself on mount, and the result was the worst
// version of both: the prompt landed dead centre and covered the only element with any context
// (the lock, the lesson's title, the button), leaving a dark rectangle behind a full-viewport
// 55% scrim. On a page that renders nothing but this player, that scrim has nothing to dim —
// the whole screen went flat grey and the page read as broken rather than as a wall.
//
// So the ask and the form are now the same object. The visitor sees one card, in the player's
// footprint, on the lesson's own thumbnail: what they came for, dimmed, with the way in on top
// of it. No second click and no second surface.
//
// THE VIDEO FILE IS NEVER LOADED BEHIND THIS. A CSS cover is not a wall — anyone could delete
// the element in devtools. The server withholds the path entirely (media_router.get_video_meta),
// so there is nothing behind the cover but a still frame. This component is presentation; the
// wall is server-side.
//
// House style, deliberate: a SOLID scrim, not a blur. backdrop-filter is banned here, and a
// blur is also weaker — it can be switched off in devtools, revealing the frame underneath.
import React from "react";
import { Lock } from "lucide-react";
import { AuthForm } from "./AuthForm";
import { useT } from "../i18n/useT";
import { apiService } from "../apiService";

/**
 * @param {string} videoId
 * @param {string} [title] - the lesson's title. Shown by the PAGE above this card, not inside
 *   it: in here it would compete with the one line that has to be read, "Sign in to watch".
 * @param {boolean} [justCreated] - true when this is the video they just made, which changes
 *   the ask from "sign in to watch this" to "sign in to watch YOURS".
 * @param {string} [className] - sizing from the parent. NOT `aspect-video`: the form sets the
 *   height here, and a fixed 16:9 box clips it on narrow windows.
 */
const LockedVideo = ({ videoId, title, justCreated = false, className = "" }) => {
  const t = useT();

  const heading = justCreated ? t("auth_watch_ready_title") : t("auth_watch_title");
  const blurb = justCreated ? t("auth_watch_ready_desc") : t("auth_watch_desc");

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gray-900 min-h-[420px] flex items-center justify-center ${className}`}
    >
      {/* Thumbnails stay public by design — they are the hook on a shared link. */}
      <img
        src={apiService.getThumbnailUrl(videoId)}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />

      {/* Denser than the 85% this used when it was only holding a thumbnail down: a sign-in
          form has to be legible on top of it, and thumbnails are bright title cards. */}
      <div className="absolute inset-0 bg-black/90" />

      <div className="relative w-full max-w-sm px-6 py-10">
        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4 mx-auto">
          <Lock className="w-5 h-5 text-white" />
        </div>

        {/* The dark palette, which is the house one — this sits on a near-black cover, and it
            is the same form the rest of the app signs in with.
            No onSuccess: signing in changes the auth state, the parent re-asks /meta on that
            change and swaps this component for the real player. Nothing to hand back to. */}
        <AuthForm compact title={heading} subtitle={blurb} />
      </div>
    </div>
  );
};

export default LockedVideo;
